import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { WindowEvent } from 'library/ts/ui/events/WindowEvent';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagEventDataPromiseReject } from 'tag/ts/tag/interfaces/events/ITagEventDataPromiseReject';
import { ITagEventDataPromiseResolve } from 'tag/ts/tag/interfaces/events/ITagEventDataPromiseResolve';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { TTagEventCallback } from 'tag/ts/tag/types/TTagEventCallback';
import { IPlayerLifecycleManager } from './interfaces/IPlayerLifecycleManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Manages player lifecycle operations and cleanup to prevent memory leaks
 *
 * **Lifecycle Management Scope:**
 * - Window event handler management (beforeunload) with proper cleanup
 * - Player destruction coordination and sequencing
 * - Tag reset orchestration with comprehensive cleanup
 * - Memory leak prevention during all lifecycle transitions
 * - Resource cleanup coordination across player components
 *
 * **Memory Leak Prevention Focus:**
 * This manager centralizes all cleanup logic that was previously scattered
 * throughout PlayerManager, ensuring reliable resource cleanup and preventing
 * the accumulation of orphaned event listeners and references.
 *
 * **Window Event Management:**
 * Implements the memory leak fix for window beforeunload listeners by:
 * - Storing handler function references for proper removal
 * - Coordinating cleanup during tag reset/removal operations
 * - Preventing accumulation of orphaned window event listeners
 *
 * **Extracted from PlayerManager:**
 * Consolidates lifecycle and cleanup logic for better maintainability,
 * focused testing, and reliable memory management.
 */
export class PlayerLifecycleManager implements IPlayerLifecycleManager {
	#logger: ILogger;
	#tag: ITag;
	#utils: Utilities;
	#initialized: boolean = false;

	// Window event handler storage for cleanup - MEMORY LEAK PREVENTION
	#windowBeforeUnloadHandler: ( ( event: Event ) => void ) | null = null;

	constructor( tag: ITag, utils: Utilities ) {
		this.#tag = tag;
		this.#utils = utils;
		this.#logger = LogService.create( {
			prefixes: [ 'SmartTag:', 'Tag:', tag?.name || 'Unknown', 'PlayerLifecycleManager:' ]
		} );
		this.#logger.log( 'PlayerLifecycleManager created' );
	}

	/**
	 * @description Initializes lifecycle management components
	 * Sets up window event handlers and other lifecycle resources
	 */
	initialize(): Promise<IPromiseResolveGeneric> {
		return new Promise( ( resolve, reject ) => {
			try {
				this.#setupWindowBeforeUnloadHandler();
				this.#initialized = true;
				this.#logger.log( 'initialize: Lifecycle management initialized successfully' );
				resolve( { msg: 'PlayerLifecycleManager initialized', success: true } );
			} catch ( error ) {
				this.#logger.error( 'initialize: Failed to initialize lifecycle management:', error );
				reject( { msg: 'PlayerLifecycleManager initialization failed', error } );
			}
		} );
	}

	/**
	 * @description Setup for window 'beforeunload' event with memory leak prevention
	 *
	 * **MEMORY LEAK PREVENTION:**
	 * Stores the event handler function reference to enable proper cleanup when
	 * PlayerManager is destroyed. Without this, each PlayerManager instance would
	 * leave an orphaned window event listener that prevents garbage collection.
	 *
	 * **Critical for Tag Applications:**
	 * Since each Tag creates its own PlayerManager, and users can create/destroy
	 * multiple Tags, this prevents accumulation of orphaned window listeners.
	 */
	#setupWindowBeforeUnloadHandler(): void {
		// Don't register if already registered
		if ( this.#windowBeforeUnloadHandler ) {
			this.#logger.warn( 'setupWindowBeforeUnloadHandler: Handler already registered, skipping' );
			return;
		}

		/**
		 * @description callback to execute after player is destroyed
		 */
		const callback = ( success: boolean, result: any ): void => {
			this.#logger.log( 'windowBeforeUnloadHandler callback: player removed:', success, result );
		};

		// **MEMORY LEAK PREVENTION:**
		// Store handler function reference for later cleanup
		this.#windowBeforeUnloadHandler = ( event: Event ) => {
			// if player exists and was not stopped (resource is not empty)
			const player = this.#tag?.controller?.playerManager?.player;
			if ( this.#utils.Type.isDefined( player ) && !this.#utils.Type.isEmpty( player?.resource ) ) {
				// request to stop player
				const playerAdapter = this.#tag?.controller?.playerManager?.playerAdapter;
				if ( playerAdapter?.requestPlayerStop ) {
					playerAdapter.requestPlayerStop().then(
						( data ) => {
							// request player removal / destruction
							this.requestPlayerDestruction( callback );
						}
					);
				}
			}
		};

		// INFO: https://jira.cbsi.com/browse/CBSVIDEO-5032
		// window beforeunload processor for stopping active player session
		// player stop method is going to stop Conviva session as well
		window.addEventListener( WindowEvent.BEFORE_UNLOAD, this.#windowBeforeUnloadHandler );

		this.#logger.log( 'setupWindowBeforeUnloadHandler: Window beforeunload handler registered' );
	}

	/**
	 * @description Cleanup window beforeunload event handler to prevent memory leaks
	 *
	 * **MEMORY LEAK PREVENTION:**
	 * Removes the window event listener that was added during setup to prevent
	 * the PlayerManager instance from being kept alive indefinitely by the
	 * global window event listener.
	 *
	 * **When to Call:**
	 * - During PlayerManager destruction/cleanup
	 * - During Tag reset operations
	 * - When PlayerManager is no longer needed
	 *
	 * **Critical for Tag Applications:**
	 * Each Tag creates its own PlayerManager with its own window listener.
	 * Without cleanup, these listeners accumulate and prevent garbage collection.
	 */
	#cleanupWindowBeforeUnloadHandler(): void {
		if ( this.#windowBeforeUnloadHandler ) {
			window.removeEventListener( WindowEvent.BEFORE_UNLOAD, this.#windowBeforeUnloadHandler );
			this.#windowBeforeUnloadHandler = null;
			this.#logger.log( 'cleanupWindowBeforeUnloadHandler: Window beforeunload handler cleaned up' );
		} else {
			this.#logger.log( 'cleanupWindowBeforeUnloadHandler: No handler to clean up' );
		}
	}

	/**
	 * @description Executes tag reset logic with comprehensive cleanup
	 *
	 * **Reset Coordination:**
	 * Orchestrates the complete tag reset process including:
	 * - Player event listener removal
	 * - Player stopping and destruction
	 * - DOM cleanup and event dispatcher reset
	 * - Window event handler cleanup (MEMORY LEAK PREVENTION)
	 */
	executeTagReset( event: TagEventVO ): void {
		this.#logger.log( 'executeTagReset: Starting tag reset process' );

		/**
		 * @description Resolve promise passed via reset event argument
		 */
		const resolveEventPromise = ( promiseResolve: ITagEventDataPromiseResolve = undefined, promiseReject: ITagEventDataPromiseReject = undefined, message: string = undefined ): void => {
			// method assignment placeholder variable
			let methodToExecute: ITagEventDataPromiseResolve | ITagEventDataPromiseReject = undefined;

			// determine method to execute - resolve first...
			if ( this.#utils.Type.isFunction( promiseResolve ) ) {
				methodToExecute = promiseResolve as ITagEventDataPromiseResolve;
			}
			// if resolve method is not a function and reject method is a function
			if ( !this.#utils.Type.isFunction( methodToExecute ) && this.#utils.Type.isFunction( promiseReject ) ) {
				// assign reject method...
				methodToExecute = promiseReject as ITagEventDataPromiseReject;
			}

			// execute method if it exists and is a function
			if ( this.#utils.Type.isFunction( methodToExecute ) ) {
				methodToExecute( { msg: message || 'Tag reset operation completed' } );
			}
		};

		/**
		 * @description Callback to execute after player is destroyed
		 */
		const playerDestroyRequestCallback = ( success: boolean, result: any ): void => {
			this.#logger.log( 'executeTagReset: Player destroy callback:', success );
			switch ( success ) {
				case true:
					if ( this.#utils.Type.isElement( this.#tag?.view?.smartTagElement ) ) {
						// sanitize Smart Tag DOM skeleton removing all DOM event listeners
						this.#utils.DOM.recreateNode( this.#tag.view.smartTagElement, true ).then(
							() => {
								// remove Smart Tag skeleton from DOM
								this.#utils.DOM.removeElement( this.#tag?.view?.smartTagElement ).then(
									( data ) => {
										this.#logger.log( 'executeTagReset: DOM element removed and destroyed' );
										// @SONAR_START_NO_SCAN@
										data.element = undefined;
										// @SONAR_END_NO_SCAN@
										delete data?.element;

										// reset event dispatcher
										this.#tag.eventDispatcher.clear().then(
											( data ) => {
												this.#logger.log( 'executeTagReset: Unsubscribed from all Tag events' );

												// **MEMORY LEAK PREVENTION:**
												// Clean up window event listener before completing reset
												// This prevents PlayerManager from being kept alive by orphaned event listener
												this.#cleanupWindowBeforeUnloadHandler();

												resolveEventPromise( event?.data?.promiseResolve, undefined, 'Tag reset complete!' );
											}
										);
									}
								);
							}
						);
					}
					break;
				case false:
					this.#logger.warn( 'executeTagReset: Player removal failed:', result );

					// **MEMORY LEAK PREVENTION:**
					// Clean up window event listener even on failure to prevent accumulation
					// Failed player removal should not prevent global resource cleanup
					this.#cleanupWindowBeforeUnloadHandler();

					resolveEventPromise( undefined, event?.data?.promiseReject, 'Tag reset failed during player removal' );
					break;
			}
		};

		// Start the reset process:
		// if player exists
		const player = this.#tag?.controller?.playerManager?.player;
		if ( this.#utils.Type.isDefined( player ) && this.#utils.Type.isFunction( player?.removePlayerEventListener ) ) {
			this.#logger.log( 'executeTagReset: Removing player event listeners' );
			// remove player event listeners
			player.removePlayerEventListener().then(
				( data ) => {
					// if resource is not empty => stop player
					if ( !this.#utils.Type.isEmpty( player?.resource ) ) {
						this.#logger.log( 'executeTagReset: Stopping player' );
						// stop player
						player.stop().then(
							( data ) => {
								this.#logger.log( 'executeTagReset: Player stopped, requesting destruction' );
								// request player removal / destruction
								this.requestPlayerDestruction( playerDestroyRequestCallback );
							}
						);
					} else {
						this.#logger.log( 'executeTagReset: No resource to stop, proceeding to destruction' );
						// request player removal / destruction
						this.requestPlayerDestruction( playerDestroyRequestCallback );
					}
				}
			);
		} else {
			this.#logger.log( 'executeTagReset: No player to reset, cleaning up directly' );

			// Remove DOM skeleton even when no player exists
			if ( this.#utils.Type.isElement( this.#tag?.view?.smartTagElement ) ) {
				// sanitize Smart Tag DOM skeleton removing all DOM event listeners
				this.#utils.DOM.recreateNode( this.#tag.view.smartTagElement, true ).then(
					() => {
						// remove Smart Tag skeleton from DOM
						this.#utils.DOM.removeElement( this.#tag?.view?.smartTagElement ).then(
							( removeData ) => {
								this.#logger.log( 'executeTagReset: DOM element removed and destroyed (no player case)' );
								// @SONAR_START_NO_SCAN@
								removeData.element = undefined;
								// @SONAR_END_NO_SCAN@
								delete removeData?.element;

								// reset event dispatcher
								this.#tag.eventDispatcher.clear().then(
									( _clearData ) => {
										this.#logger.log( 'executeTagReset: Unsubscribed from all Tag events (no player case)' );

										// **MEMORY LEAK PREVENTION:**
										// Clean up window event listener before completing reset
										this.#cleanupWindowBeforeUnloadHandler();

										resolveEventPromise( event?.data?.promiseResolve, undefined, 'Tag reset complete (no player)!' );
									}
								);
							}
						);
					}
				);
			} else {
				// **MEMORY LEAK PREVENTION:**
				// Still clean up window handlers even if no DOM element exists
				this.#cleanupWindowBeforeUnloadHandler();
				resolveEventPromise( event?.data?.promiseResolve, undefined, 'Tag reset complete (no player, no DOM)!' );
			}
		}
	}

	/**
	 * @description Requests player destruction with callback coordination
	 */
	requestPlayerDestruction( callback: TTagEventCallback ): void {
		this.#logger.log( 'requestPlayerDestruction: Dispatching destroy request' );
		// request player removal / destruction
		this.#tag.dispatchTagEvent(
			this.#tag.getTagEventVO(
				TagEvent.ON_DESTROY_PLAYER_REQUEST,
				this.#tag,
				{
					eventCallback: callback
				},
				this,
				'PlayerLifecycleManager'
			)
		);
	}

	/**
	 * @description Performs complete cleanup of all lifecycle resources
	 *
	 * **Memory Leak Prevention:**
	 * Ensures all lifecycle-managed resources are properly cleaned up:
	 * - Window event listeners removed
	 * - Event handlers unregistered
	 * - References nullified for garbage collection
	 */
	cleanup(): void {
		this.#logger.log( 'cleanup: Starting lifecycle cleanup' );

		// Clean up window event handlers
		this.#cleanupWindowBeforeUnloadHandler();

		// Mark as not initialized
		this.#initialized = false;

		this.#logger.log( 'cleanup: Lifecycle cleanup completed' );
	}

	/**
	 * @description Returns whether lifecycle management is properly initialized
	 */
	isInitialized(): boolean {
		return this.#initialized;
	}
}